(() => {
  const roots = document.querySelectorAll('[data-wiring-diagram-generator]');
  if (!roots.length) return;

  const cleanTitle = (title) => title.replace(/^Wiring Diagram Generator:\s*/i, '');
  const labelMap = {
    over_current: 'Over-current protection',
    external_limits: 'External limit switches',
    speed_control: 'Speed control'
  };
  const controlIcons = {
    'Rocker switch': 'SW',
    '2 Channel Remote Control': '2CH',
    '4 Channel Remote Control': '4CH',
    'FCB-1 / FCB-2 control box': 'FCB',
    'Speed Control only': 'SC'
  };
  const movementLabels = {
    'single actuator': 'Single actuator',
    'move together': 'Together - switch/control box',
    'move together through relays': 'Together - remote + relays',
    'move separately / independent channels': 'Move separately',
    'move together, synchronized': 'Synchronized'
  };
  const fcbProductLinks = {
    fcb1: 'https://www.firgelliauto.com/products/synchronous-control-board',
    fcb2: 'https://www.firgelliauto.com/products/remote-control-actuator-controller-with-lcd-screen'
  };
  const partLinks = [
    { test: /linear actuators?/i, url: 'https://www.firgelliauto.com/collections/linear-actuators' },
    { test: /12V DC power/i, url: 'https://www.firgelliauto.com/collections/power-supplies' },
    { test: /Rocker switch/i, url: 'https://www.firgelliauto.com/collections/switches' },
    { test: /2 Channel Remote Control|4 Channel Remote Control/i, url: 'https://www.firgelliauto.com/collections/remotes' },
    { test: /FCB-1|FCB-2/i, url: 'https://www.firgelliauto.com/collections/control-systems-and-remotes' },
    { test: /FA-POCT|over-current|external limit|FA-SC1|speed controller/i, url: 'https://www.firgelliauto.com/collections/actuator-switches-remotes-relays-timers-controllers-power-supplies' },
    { test: /SPDT relays?/i, url: 'https://www.firgelliauto.com/collections/relays' }
  ];
  const movementOrder = ['single actuator', 'move together', 'move together through relays', 'move separately / independent channels', 'move together, synchronized'];
  const controlOrder = ['Rocker switch', '2 Channel Remote Control', '4 Channel Remote Control', 'FCB-1 / FCB-2 control box', 'Speed Control only'];
  const featureKeys = ['over_current', 'external_limits', 'speed_control'];

  roots.forEach(async (root) => {
    const catalogUrl = root.dataset.catalogUrl;
    const els = {
      count: root.querySelector('[data-wdg-count]'),
      actuators: root.querySelector('[data-wdg-actuators]'),
      movementGroup: root.querySelector('[data-wdg-movement-group]'),
      movement: root.querySelector('[data-wdg-movement]'),
      control: root.querySelector('[data-wdg-control]'),
      title: root.querySelector('[data-wdg-title]'),
      image: root.querySelector('[data-wdg-image]'),
      pdf: root.querySelector('[data-wdg-pdf]'),
      parts: root.querySelector('[data-wdg-parts]'),
      connects: root.querySelector('[data-wdg-connects]'),
      matches: root.querySelector('[data-wdg-matches]'),
      search: root.querySelector('[data-wdg-search]'),
      filters: root.querySelector('[data-wdg-index-filters]'),
      list: root.querySelector('[data-wdg-list]')
    };

    let diagrams = [];
    let state = {
      actuators: 1,
      movement: 'single actuator',
      control: 'Rocker switch',
      features: { over_current: false, external_limits: false, speed_control: false },
      selectedId: null,
      indexFilter: 'all',
      query: ''
    };

    try {
      const response = await fetch(catalogUrl, { credentials: 'same-origin' });
      diagrams = (await response.json()).map(normalizeDiagram);
    } catch (error) {
      els.title.textContent = 'Diagram catalog could not load';
      return;
    }

    els.count.textContent = diagrams.length + ' active diagrams';
    createActuatorButtons();
    createIndexFilters();
    syncAvailableState();
    renderAll();

    root.querySelectorAll('[data-wdg-feature]').forEach((input) => {
      input.addEventListener('change', () => {
        state.features[input.dataset.wdgFeature] = input.checked;
        state.selectedId = null;
        syncAvailableState();
        renderAll();
      });
    });
    els.search.addEventListener('input', () => {
      state.query = els.search.value.trim().toLowerCase();
      renderIndex();
    });

    function createActuatorButtons() {
      const counts = unique(diagrams.map((d) => d.actuators)).sort((a, b) => a - b);
      els.actuators.innerHTML = counts.map((count) => buttonHtml(String(count), count, state.actuators === count)).join('');
      els.actuators.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          state.actuators = Number(button.value);
          state.selectedId = null;
          syncAvailableState();
          renderAll();
        });
      });
    }

    function createMovementButtons(options) {
      els.movement.innerHTML = options.map((movement) => buttonHtml(movementLabels[movement] || movement, movement, state.movement === movement)).join('');
      els.movement.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          state.movement = button.value;
          state.selectedId = null;
          syncAvailableState();
          renderAll();
        });
      });
    }

    function createControlButtons(options) {
      els.control.innerHTML = options.map((control) => {
        const active = state.control === control;
        return '<button class="wdg__option" type="button" value="' + escapeAttr(control) + '" aria-pressed="' + active + '"><span class="wdg__option-icon">' + (controlIcons[control] || '') + '</span><span>' + control + '</span></button>';
      }).join('');
      els.control.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          state.control = button.value;
          state.selectedId = null;
          syncAvailableState();
          renderAll();
        });
      });
    }

    function syncAvailableState() {
      const byActuator = diagrams.filter((d) => d.actuators === state.actuators);
      const movements = movementOrder.filter((movement) => byActuator.some((d) => d.movement === movement));
      if (!movements.includes(state.movement)) state.movement = movements[0] || 'single actuator';
      els.movementGroup.hidden = state.actuators === 1;
      createMovementButtons(movements);

      const byMovement = byActuator.filter((d) => d.movement === state.movement || state.actuators === 1);
      const controls = controlOrder.filter((control) => byMovement.some((d) => d.control === control));
      if (!controls.includes(state.control)) state.control = controls[0] || controls[0];
      createControlButtons(controls);

      root.querySelectorAll('[data-wdg-feature]').forEach((input) => {
        const key = input.dataset.wdgFeature;
        const possible = byMovement.some((d) => d.control === state.control && d[key]);
        input.disabled = !possible;
        if (!possible) {
          input.checked = false;
          state.features[key] = false;
        }
      });
    }

    function renderAll() {
      updatePressed(els.actuators, String(state.actuators));
      updatePressed(els.movement, state.movement);
      updatePressed(els.control, state.control);
      renderResult();
      renderIndex();
    }

    function matchingDiagrams() {
      return diagrams.filter((d) => {
        if (d.actuators !== state.actuators) return false;
        if (state.actuators > 1 && d.movement !== state.movement) return false;
        if (d.control !== state.control) return false;
        return featureKeys.every((key) => state.features[key] ? d[key] : true);
      }).sort((a, b) => scoreDiagram(a) - scoreDiagram(b));
    }

    function scoreDiagram(d) {
      return featureKeys.reduce((score, key) => score + (d[key] === state.features[key] ? 0 : 1), 0);
    }

    function renderResult() {
      const matches = matchingDiagrams();
      const selected = matches.find((d) => d.id === state.selectedId) || matches[0];
      if (!selected) {
        els.title.textContent = 'No matching diagram';
        els.image.removeAttribute('src');
        els.pdf.href = '#';
        els.parts.innerHTML = '';
        els.connects.textContent = 'Try removing one add-on or choosing another control method.';
        els.matches.innerHTML = '';
        return;
      }
      state.selectedId = selected.id;
      els.title.textContent = cleanTitle(selected.original_title);
      els.image.src = selected.jpeg;
      els.pdf.href = selected.pdf;
      els.parts.innerHTML = renderParts(selected);
      els.connects.textContent = selected.connects;
      els.matches.innerHTML = matches.length > 1 ? matches.slice(0, 8).map((diagram) => {
        const label = diagram.figure ? 'Fig. ' + diagram.figure : 'Diagram ' + diagram.id;
        return '<button class="wdg__match" type="button" value="' + diagram.id + '" aria-current="' + (diagram.id === selected.id) + '">' + label + '</button>';
      }).join('') : '';
      els.matches.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          state.selectedId = Number(button.value);
          renderResult();
        });
      });
    }

    function createIndexFilters() {
      const filters = ['all', '1 actuator', '2 actuator', '3 actuator', '4 actuator', 'remote', 'rocker', 'limits', 'speed'];
      els.filters.innerHTML = filters.map((filter) => '<button class="wdg__chip" type="button" value="' + filter + '" aria-pressed="' + (filter === state.indexFilter) + '">' + labelFilter(filter) + '</button>').join('');
      els.filters.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          state.indexFilter = button.value;
          updatePressed(els.filters, state.indexFilter);
          renderIndex();
        });
      });
    }

    function renderIndex() {
      updatePressed(els.filters, state.indexFilter);
      const rows = diagrams.filter((d) => indexMatches(d));
      if (!rows.length) {
        els.list.innerHTML = '<div class="wdg__empty">No diagrams match that search.</div>';
        return;
      }
      els.list.innerHTML = rows.map((d) => {
        const tags = [d.actuators + ' actuator' + (d.actuators === 1 ? '' : 's'), d.control]
          .concat(featureKeys.filter((key) => d[key]).map((key) => labelMap[key]));
        return '<article class="wdg__item"><div><p class="wdg__item-title">' + escapeHtml(cleanTitle(d.original_title)) + '</p><div class="wdg__tags">' + tags.map((tag) => '<span class="wdg__tag">' + escapeHtml(tag) + '</span>').join('') + '</div></div><div class="wdg__item-actions"><button class="wdg__small-button" type="button" data-view-id="' + d.id + '">View</button><a class="wdg__small-button" href="' + escapeAttr(d.pdf) + '" target="_blank" rel="noopener">PDF</a></div></article>';
      }).join('');
      els.list.querySelectorAll('[data-view-id]').forEach((button) => {
        button.addEventListener('click', () => {
          const diagram = diagrams.find((d) => d.id === Number(button.dataset.viewId));
          if (!diagram) return;
          state.actuators = diagram.actuators;
          state.movement = diagram.movement;
          state.control = diagram.control;
          featureKeys.forEach((key) => state.features[key] = diagram[key]);
          root.querySelectorAll('[data-wdg-feature]').forEach((input) => input.checked = state.features[input.dataset.wdgFeature]);
          state.selectedId = diagram.id;
          syncAvailableState();
          renderAll();
          root.querySelector('.wdg__builder').scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    function indexMatches(d) {
      const haystack = [d.original_title, d.control, d.movement, d.main_parts, d.connects].join(' ').toLowerCase();
      if (state.query && !haystack.includes(state.query)) return false;
      switch (state.indexFilter) {
        case '1 actuator': return d.actuators === 1;
        case '2 actuator': return d.actuators === 2;
        case '3 actuator': return d.actuators === 3;
        case '4 actuator': return d.actuators === 4;
        case 'remote': return d.control.includes('Remote');
        case 'rocker': return d.control === 'Rocker switch';
        case 'limits': return d.external_limits;
        case 'speed': return d.speed_control;
        default: return true;
      }
    }

    function renderParts(diagram) {
      const parts = diagram.main_parts.split(';').map((part) => part.trim()).filter(Boolean);
      if (diagram.movement === 'move together through relays' && diagram.control === '2 Channel Remote Control' && diagram.actuators > 1) {
        parts.push('2 SPDT relays');
      }

      return parts.map((part) => {
        if (/FCB-1\s*\/\s*FCB-2/i.test(part)) {
          return '<li><a href="' + escapeAttr(fcbProductLinks.fcb1) + '" target="_blank" rel="noopener">FCB-1</a> / <a href="' + escapeAttr(fcbProductLinks.fcb2) + '" target="_blank" rel="noopener">FCB-2</a> control box (built-in speed control; FCB-2 includes wireless remotes)</li>';
        }
        const link = partLinks.find((candidate) => candidate.test.test(part));
        const label = escapeHtml(part);
        if (!link) return '<li>' + label + '</li>';
        return '<li><a href="' + escapeAttr(link.url) + '" target="_blank" rel="noopener">' + label + '</a></li>';
      }).join('');
    }

    function normalizeDiagram(diagram) {
      const visibleText = diagram.original_title + ' ' + diagram.main_parts + ' ' + diagram.connects + ' ' + diagram.control + ' ' + diagram.jpeg + ' ' + diagram.pdf;
      const isFcbDiagram = /FCB-1|FCB-2|FA-SYNC|Synchronous Control Board|wiring-diagram-\d-FCB|FA-SYNC-4/i.test(visibleText);
      if (!isFcbDiagram) return diagram;

      const normalized = Object.assign({}, diagram);
      if (diagram.figure === '52' || /FA-SYNC|Synchronous Control Board/i.test(visibleText)) {
        normalized.original_title = 'Wiring Diagram Generator: 4 actuator - 2 Channel Remote Control - Over Current Protection (FA-POCT) - FCB-1 / FCB-2 control box';
        normalized.control = '2 Channel Remote Control';
      }

      normalized.speed_control = true;
      normalized.external_limits = false;
      normalized.main_parts = (normalized.main_parts || '')
        .replace(/;\s*FA-SYNC-4/ig, '; FCB-1 / FCB-2 control box')
        .replace(/;\s*external limit switch kit \+ diodes/ig, '')
        .replace(/;\s*FA-SC1 speed controller/ig, '');
      if (!/FCB-1\s*\/\s*FCB-2/i.test(normalized.main_parts)) {
        normalized.main_parts += '; FCB-1 / FCB-2 control box';
      }
      normalized.connects = '12V power and the external 2 Channel Remote Control shown in this diagram feed the FCB-1 or FCB-2 control box; each actuator motor/feedback lead lands on its own FCB output. Speed control is built into the FCB controller. Programmable position/limit-stop control is handled in the FCB settings and requires feedback actuators. FCB-2 includes wireless remotes; FCB-1 can be controlled from the FCB settings or by connecting a separate switch, which is not shown in this diagram.';
      return normalized;
    }

    function updatePressed(container, value) {
      container.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.value === value)));
    }
  });

  function buttonHtml(label, value, active) {
    return '<button class="wdg__option" type="button" value="' + escapeAttr(value) + '" aria-pressed="' + active + '"><span>' + escapeHtml(label) + '</span></button>';
  }
  function unique(values) { return [...new Set(values.filter((value) => value !== null && value !== undefined))]; }
  function labelFilter(filter) { return filter === 'all' ? 'All diagrams' : filter.charAt(0).toUpperCase() + filter.slice(1); }
  function escapeHtml(value) { return String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])); }
  function escapeAttr(value) { return escapeHtml(value); }
})();
